import { setSentryUser } from './instrument';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Iframe } from 'features/iframe/components/Iframe';
import { isIframeStyleOrItemDetailPath } from 'features/iframe/utils';
import { usePageView } from 'lib/segment';
import { LoginContext } from 'features/auth/types';
import { useUser } from 'features/auth/hooks/useUser';
import { AccessControl } from 'features/accessControl/components/AccessControl';
import { RouterFallback } from 'features/accessControl/components/Fallback';
import { useOrgType } from 'features/accessControl/hooks/useOrg';
import { getRouteScope } from 'features/accessControl/utils/routeScopes';
import { RenderProfiler } from 'lib/profiling/RenderProfiler';
import { SEGMENT_ANALYTICS_KEY, SEGMENT_ENV, SEGMENT_COOKIE_DOMAIN } from 'config';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createMockAnalytics } from 'lib/segment/mockAnalytics';
import { loadHubSpot } from 'lib/hubspot';
import { Layout } from 'components/layout/Layout';
import { ReloadPrompt } from 'components/reloadPrompt/ReloadPrompt';

const IS_MOCK_ANALYTICS = SEGMENT_ANALYTICS_KEY === 'mock';
export const analytics = IS_MOCK_ANALYTICS ? createMockAnalytics() : new AnalyticsBrowser();

if (SEGMENT_ANALYTICS_KEY) {
  analytics.load(
    {
      writeKey: SEGMENT_ANALYTICS_KEY,
      cdnURL: 'https://cdn.segment.com',
    },
    {
      cookie: {
        domain: SEGMENT_COOKIE_DOMAIN,
      },
    }
  );
}

export const identify = (u: LoginContext) => {
  if (SEGMENT_ANALYTICS_KEY && u) {
    analytics.identify(`${SEGMENT_ENV}:${u.id}`, {
      name: u.name,
      email: u.email,
      createdAt: u.signUpDate,
      roles: u.roleNames,
      environment: SEGMENT_ENV,
      loggedInUserId: u.id,
      loggedInUserOrganizationId: String(u.organizationId),
      loggedInUserOrganizationType: u.organizationType === 1 ? 'Designer' : 'Supplier',
    });

    // Group user with their organization
    analytics.group(`${SEGMENT_ENV}:${u.organizationId.toString()}`, {
      name: u.organizationName,
      type: u.organizationType === 1 ? 'Designer' : 'Supplier',
      plan: u.planType,
      plan_tier: u.planTier,
      environment: SEGMENT_ENV,
      loggedInUserId: u.id,
      loggedInUserOrganizationId: String(u.organizationId),
    });
  }

  // Set Sentry user to match analytics identify
  setSentryUser(u);
};

export const App = ({ topbarInviteActions }: { topbarInviteActions?: React.ReactNode }) => {
  const location = useLocation();
  const { data } = useUser();
  const isDesigner = useOrgType(1);
  const [hideTopbar, setHideTopbar] = useState(false);

  useEffect(() => {
    if (!isIframeStyleOrItemDetailPath(location.pathname)) {
      setHideTopbar(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (data?.loginContext) {
      identify(data.loginContext);
      loadHubSpot(data.loginContext.organizationType === 1, data.loginContext.email);
    }
  }, [data]);

  // Register navigation to segment in an SPA
  usePageView(Boolean(SEGMENT_ANALYTICS_KEY));

  // Set app version for Segment
  window.app_version = APP_VERSION;

  return (
    <Layout hideTopbar={hideTopbar} topbarInviteActions={topbarInviteActions}>
      <AccessControl scope={getRouteScope(location.pathname)} fallback={<RouterFallback />}>
        <RenderProfiler id={location.pathname}>
          <Outlet />
        </RenderProfiler>
        {isDesigner && <Iframe path={location.pathname} onDetailFullscreenChange={setHideTopbar} />}
        <ReloadPrompt />
      </AccessControl>
    </Layout>
  );
};
